import type { HTMLAttributes, ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'

import { isNil } from 'ramda'
import useResizeObserver from 'use-resize-observer/polyfilled'

import { Paper } from '@twisto/components/atoms/paper'
import { useScrollPosition } from '@twisto/hooks'

import * as styles from './dropdown-list.styles'

type Props = {
  children: ReactElement | ReactElement[]
  className?: string
  opened?: boolean
  position?: 'below' | 'above' | 'auto'
} & HTMLAttributes<HTMLDivElement>

export const DropDownList = ({
  children,
  className,
  opened = false,
  position: defaultPosition = 'auto',
}: Props) => {
  const [position, setPosition] =
    useState<typeof defaultPosition>(defaultPosition)
  const scrollPosition = useScrollPosition()
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const { height = 0 } = useResizeObserver({ ref: dropdownRef })

  useEffect(() => {
    const rect = dropdownRef.current?.getBoundingClientRect()

    if (isNil(rect) || defaultPosition !== 'auto') {
      return
    }
    const heightOfWindow = window.innerHeight
    const parentBottom =
      dropdownRef.current?.offsetParent?.getBoundingClientRect().bottom ?? 0
    const parentTop =
      dropdownRef.current?.offsetParent?.getBoundingClientRect().top ?? 0

    const canFitBelow = parentBottom + height <= heightOfWindow
    const canFitAbove = parentTop - height >= 0

    // if it can't fit below but can fit above, display it above
    if (!canFitBelow && canFitAbove) {
      setPosition('above')
    } else {
      setPosition('below')
    }
  }, [scrollPosition, defaultPosition, height])

  if (!opened) {
    return null
  }

  return (
    <div
      ref={dropdownRef}
      css={[styles.root, position === 'above' && styles.abovePositioned]}
    >
      <Paper noMargin noPadding className={className} css={styles.content}>
        <ul css={styles.listRoot} data-testid="dropdown-list">
          {children}
        </ul>
      </Paper>
    </div>
  )
}
